import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Bandage, Candy, Droplet, Droplets, FlaskConical, FlaskRound, Pill, SprayCan, Syringe, Tablets, TestTubeDiagonal, type LucideIcon } from "lucide-react";
import { cn } from "@/shared/lib/utils";
type IconProps = {
  className?: string;
};
type IconComponent = LucideIcon | React.ComponentType<IconProps>;
const svgBaseProps = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const
};

// Squeezable tube — ointment / cream / gel / lotion / paste.
const TubeIcon = t0 => {
  const $ = _c(7);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("lucide", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M7 4h10l-1 3H8z" />;
    t3 = <path d="M8 7h8v11a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2z" />;
    t4 = <path d="M11 11h2" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1) {
    t5 = <svg {...svgBaseProps} className={t1}>{t2}{t3}{t4}</svg>;
    $[5] = t1;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};

// Granules — small grains inside a rounded container.
const GranuleIcon = t0 => {
  const $ = _c(10);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("lucide", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <rect x="4" y="5" width="16" height="15" rx="3" />;
    t3 = <path d="M4 9h16" />;
    t4 = <circle cx="9" cy="14" r="0.6" fill="currentColor" />;
    t5 = <circle cx="13" cy="13" r="0.6" fill="currentColor" />;
    t6 = <circle cx="15" cy="16" r="0.6" fill="currentColor" />;
    t7 = <circle cx="10" cy="17" r="0.6" fill="currentColor" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t1) {
    t8 = <svg {...svgBaseProps} className={t1}>{t2}{t3}{t4}{t5}{t6}{t7}</svg>;
    $[8] = t1;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
};

// L-shaped inhaler silhouette.
const InhalerIcon = t0 => {
  const $ = _c(6);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("lucide", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M6 4h8a2 2 0 0 1 2 2v6h2a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2v-4H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />;
    t3 = <path d="M16 14h2" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <svg {...svgBaseProps} className={t1}>{t2}{t3}</svg>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const ICON_BY_FORM: Record<string, IconComponent> = {
  tablet: Tablets,
  capsule: Pill,
  syrup: FlaskRound,
  solution: Droplet,
  suspension: FlaskConical,
  drops: Droplets,
  injection: Syringe,
  infusion: Syringe,
  powder: TestTubeDiagonal,
  granule: GranuleIcon,
  ointment: TubeIcon,
  cream: TubeIcon,
  gel: TubeIcon,
  lotion: TubeIcon,
  paste: TubeIcon,
  inhaler: InhalerIcon,
  aerosol: SprayCan,
  spray: SprayCan,
  suppository: Pill,
  pessary: Pill,
  enema: Droplets,
  patch: Bandage,
  pastille: Candy
};
export type DosageFormIconProps = {
  form?: string | null;
  className?: string;
};
export function DosageFormIcon(t0) {
  const $ = _c(3);
  const {
    form,
    className
  } = t0;
  const key = form?.trim().toLowerCase() ?? "";
  const Icon = ICON_BY_FORM[key] ?? Pill;
  let t1;
  if ($[0] !== Icon || $[1] !== className) {
    t1 = <Icon className={className} />;
    $[0] = Icon;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}